import { EmptyState } from "@/design-system/components/ui";
import type { DashboardWidgetDto } from "@/types/api";
import { WIDGET_CATALOG } from "./catalog";
import { WidgetBoundary, WidgetFrame } from "./WidgetFrame";

/**
 * Renders the server-ordered, permission-filtered widget list for the active
 * role into a responsive grid. The server is the ordering + gating authority
 * (per-caller claims); the engine only maps keys to components. Unknown keys
 * are skipped so the API can ship widgets ahead of the client.
 */
export function DashboardEngine({
  widgets,
  activeRole,
  isLoading,
  isError,
}: {
  widgets: DashboardWidgetDto[] | undefined;
  activeRole: string;
  isLoading: boolean;
  isError: boolean;
}) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-space-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="sm:col-span-1 xl:col-span-2">
            <WidgetFrame title="Loading" isLoading />
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return <WidgetFrame title="Dashboard" isError />;
  }

  const known = (widgets ?? []).filter((w) => WIDGET_CATALOG[w.key]);

  if (known.length === 0) {
    return (
      <EmptyState
        title="No role widgets yet"
        description="This role doesn't have dashboard widgets yet — your personal feed on the right still has everything assigned to you."
      />
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-space-4 items-start">
      {known.map((w) => {
        const Component = WIDGET_CATALOG[w.key].component;
        return (
          <div key={w.key} className={spanClass(w.size)}>
            <WidgetBoundary title={w.title}>
              <Component widget={w} activeRole={activeRole} />
            </WidgetBoundary>
          </div>
        );
      })}
    </div>
  );
}

function spanClass(size: DashboardWidgetDto["size"]): string {
  switch (size) {
    case "full":
      return "sm:col-span-2 xl:col-span-4";
    case "half":
      return "sm:col-span-1 xl:col-span-2";
    default:
      return "sm:col-span-1 xl:col-span-1";
  }
}
